import { getAuthRoles, getLoggedInUserGroupMembers, getMyMemberships, getUserInfo, login } from 'API/auth'
import { getMyTeams } from 'API/team'
import { ActiveProfileType } from 'configs/constants/enums/auth'
import { AuthenticateParams } from 'configs/constants/enums'
import { AuthRoleIdEnum } from 'constants/user'
import Errors from 'configs/errors'
import { AuthRole } from 'interfaces/auth'
import isEmpty from 'lodash/isEmpty'
import trim from 'lodash/trim'
import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import { AuthUserProfile, ILoginRequest, MembershipInfo } from 'types/auth'
import { isValidEmail } from 'utils/common'
import { getRoleRank, meetsMinRole } from 'utils/auth/role'
import { fcmTokenManager } from 'services/fcmTokenManager.service'
import { toast } from 'react-toastify'
import { clearAuthClientPersistence } from 'constants/auth-session'
import { EOrganizationType } from 'constants/organization-type'
import { clearOrgScopedStores } from 'utils/clear-org-scoped-stores'
import routes from 'routes'

/** Stored in place of an id when the user picks the aggregate view. */
const ALL_TEAMS = 'all'

export default class AuthStore {
  rootStore: RootStore

  accessToken: string = ''
  fcmToken: string = ''
  resetPasswordToken?: string
  userDetail: AuthUserProfile = {} as AuthUserProfile
  authRoles: AuthRole[] = []
  isDisabled: boolean = false
  isShowSurveyPopup: boolean = true

  /** Active organization ID — set after login via org picker or auto-select */
  activeOrgId: number | null = null
  /**
   * Active seasonal Team, for the pages whose data belongs to a Team rather than
   * to the organization (Schedule, Assignments). Always a Team inside
   * `activeOrgId`: switching organization clears it, because a Team from the
   * previous organization would scope those pages to a tenant the user just left.
   */
  activeTeamId: number | null = null
  /**
   * "All teams" — the union across the organization's Teams, chosen deliberately.
   *
   * Separate from `activeTeamId === null`, which means nothing is chosen yet and
   * the consumer should land on a Team. Without the distinction, walking from
   * Schedule to Assignments would re-pick the first Team every time and the user
   * could never stay on the aggregate view.
   */
  isAllTeams: boolean = false
  /** Current membership for the active org */
  currentMembership: MembershipInfo | null = null
  /** All memberships returned from login */
  memberships: MembershipInfo[] = []
  /**
   * OrgMembership.id for the current user in the active org. Resolved from
   * GET /users/me/teams once per org switch. Used wherever the BE expects an
   * ownerMembershipId filter (e.g. the process aggregate pipeline).
   */
  currentMembershipId: number | null = null
  /** True when initial auth + membership bootstrap has completed */
  isAuthBootstrapped: boolean = false

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  /**
   * Highest-rank role the user holds across all memberships. Falls back to
   * PLAYER when no memberships exist so consumers never get `undefined`.
   * Use this — not `currentMembership.roleId` — for sidebar visibility and
   * any cross-org capability check.
   */
  get maxRoleAcrossTeams(): AuthRoleIdEnum {
    const memberships = this.memberships ?? []
    if (memberships.length === 0) return AuthRoleIdEnum.PLAYER
    let best: AuthRoleIdEnum = AuthRoleIdEnum.PLAYER
    let bestRank = getRoleRank(best)
    for (const m of memberships) {
      const rank = getRoleRank(m.roleId)
      if (rank < bestRank) {
        bestRank = rank
        best = m.roleId as AuthRoleIdEnum
      }
    }
    return best
  }

  /** True when the user holds `min`-or-higher rank in at least one membership. */
  hasRoleAtLeast(min: AuthRoleIdEnum): boolean {
    return meetsMinRole(this.maxRoleAcrossTeams, min)
  }

  /**
   * Organization Type of an Organization the user belongs to — the source of its
   * Class and Team Level option lists.
   *
   * Read from the membership rather than `organizationStore.organization`, which
   * is only hydrated by the subdomain and Appearance flows and is never updated
   * on an organization switch: a multi-org user would otherwise be offered the
   * previous organization's levels, or none at all on the main domain.
   *
   * `orgId` defaults to the active organization; pass one explicitly on a page
   * scoped to a specific Organization (a Team's own, say). Undefined when the
   * organization is unknown or predates the field — callers fall back to "Other".
   */
  orgTypeOf(orgId: number | null | undefined = this.activeOrgId): EOrganizationType | undefined {
    if (!orgId) return undefined
    return this.memberships.find(m => m.orgId === orgId)?.orgType
  }

  /** Memberships where the user's role meets or exceeds `min`. Used by Phase 03/04 pickers. */
  teamsWhereRole(min: AuthRoleIdEnum): MembershipInfo[] {
    return (this.memberships ?? []).filter(m => meetsMinRole(m.roleId, min))
  }

  /**
   * True when the user has ADMIN-level rank (Admin OR Owner) in at least one
   * membership. Delegates to `hasRoleAtLeast` so OWNER-only users return true
   * — the literal-ADMIN check would miss them.
   */
  get hasAdminMembership(): boolean {
    return this.hasRoleAtLeast(AuthRoleIdEnum.ADMIN)
  }

  /**
   * Active org id with a synchronous fallback to the persisted value.
   * `activeOrgId` is only populated after `bootstrapAuthSession` resolves
   * memberships (async) — on a direct detail-page visit, uploads can start
   * before that finishes. localStorage is written at login/team-switch, so it
   * is the earliest reliable source; never returns 0.
   */
  get resolvedActiveOrgId(): number | null {
    if (this.activeOrgId) return this.activeOrgId
    const persisted = Number(localStorage.getItem(AuthenticateParams.ORG_ID))
    return Number.isFinite(persisted) && persisted > 0 ? persisted : null
  }

  public async getMyUser(): Promise<void> {
    const accessToken: string = localStorage.getItem(AuthenticateParams.ACCESS_TOKEN) || ''
    if (accessToken) {
      this.accessToken = accessToken
      try {
        const userInfo = await getUserInfo()
        const groupMembers = await getLoggedInUserGroupMembers()
        this.rootStore.groupStore.setGroupMembers(groupMembers)
        this.userDetail = userInfo
        this.rootStore.userStore.setCurrentUserGroupMembers(groupMembers)
        this.rootStore.userStore.setCurrentUser(userInfo)
      } catch (error: unknown) {
        const e = error as { response?: { status?: number }; message?: string }
        if (e?.response?.status === 401) {
          toast.warning('Login session expired!')
          this.logout()
          return
        }
        // Network/server errors — log but don't throw, let app continue to login
        console.warn('[AuthStore] getMyUser failed, clearing session:', e?.message)
        this.logout()
      }
    }
  }

  /** Bootstrap auth state on app load before route guards evaluate role */
  public async bootstrapAuthSession(): Promise<void> {
    this.isAuthBootstrapped = false
    const accessToken: string = localStorage.getItem(AuthenticateParams.ACCESS_TOKEN) || ''
    if (!accessToken) {
      this.isAuthBootstrapped = true
      return
    }
    try {
      await this.getMyUser()
      await this.fetchMyMemberships()
      this.hydrateOrgContext()
    } catch (error) {
      console.warn('[AuthStore] bootstrapAuthSession failed:', error)
    } finally {
      this.isAuthBootstrapped = true
    }
  }

  public setAccessToken(accessToken: string): void {
    this.accessToken = accessToken
    localStorage.setItem(AuthenticateParams.ACCESS_TOKEN, accessToken)
  }

  public setFCMToken(token: string): void {
    this.fcmToken = token
  }

  /**
   * Set active org context — updates activeOrgId, currentMembership, persists to localStorage.
   * Called after org picker selection or auto-select for single membership.
   * Also fires an async resolution of the OrgMembership.id (currentMembershipId)
   * for the active org so consumers can use it for ownerMembershipId filters.
   */
  public setActiveOrg(orgId: number): void {
    // A Team belongs to one organization, so the Team context cannot survive the
    // switch. Cleared rather than remapped: the consumer picks the new default.
    if (orgId !== this.activeOrgId) this.clearTeamContext()
    this.activeOrgId = orgId
    this.currentMembership = this.memberships.find(m => m.orgId === orgId) ?? null
    localStorage.setItem(AuthenticateParams.ORG_ID, orgId.toString())
    // Fire-and-forget — callers that need currentMembershipId should guard against null.
    void this.fetchCurrentMembershipId(orgId)
  }

  /**
   * Set the Team context shared by the Team-scoped pages. `null` is the explicit
   * "All teams" choice, not the absence of one — see `clearTeamContext`.
   */
  public setActiveTeam(teamId: number | null): void {
    this.activeTeamId = teamId
    this.isAllTeams = teamId === null
    localStorage.setItem(AuthenticateParams.TEAM_ID, teamId === null ? ALL_TEAMS : teamId.toString())
  }

  /** Back to "nothing chosen", so the next Team-scoped page lands on a Team. */
  public clearTeamContext(): void {
    this.activeTeamId = null
    this.isAllTeams = false
    localStorage.removeItem(AuthenticateParams.TEAM_ID)
  }

  /**
   * Resolves the OrgMembership.id (membershipId) for the given org by calling
   * GET /users/me/teams, which is the only existing endpoint that carries that ID.
   * Result is cached in `currentMembershipId`. Safe to call multiple times — the
   * network call is cheap and results are stable per org switch.
   */
  public async fetchCurrentMembershipId(orgId: number): Promise<void> {
    try {
      const teams = await getMyTeams()
      const match = teams.find(t => t.orgId === orgId && t.membershipId != null)
      this.currentMembershipId = match?.membershipId ?? null
    } catch {
      // Non-fatal — currentMembershipId stays null; callers guard against this.
      this.currentMembershipId = null
    }
  }

  /**
   * Hydrate org context from localStorage on app load.
   * Validates saved orgId still exists in current memberships.
   */
  public hydrateOrgContext(): void {
    // Org context must follow the active profile's memberships. Clear when the
    // profile has no org; otherwise keep the saved org if it's still valid for
    // this profile, else default to the first membership so single-org-scoped
    // calls always carry a orgId (and the org re-derives on profile switch).
    if (this.memberships.length === 0) {
      this.activeOrgId = null
      this.currentMembership = null
      this.clearTeamContext()
      localStorage.removeItem(AuthenticateParams.ORG_ID)
      return
    }
    const savedOrgId = Number(localStorage.getItem(AuthenticateParams.ORG_ID))
    const savedValid = Number.isFinite(savedOrgId) && this.memberships.some(m => m.orgId === savedOrgId)
    this.setActiveOrg(savedValid ? savedOrgId : this.memberships[0].orgId)

    // Restored after the org, not before: `setActiveOrg` clears the Team context,
    // so on a reload it would otherwise wipe the very value being restored. The
    // Team is not validated here — the consumer holds the org's Team list and
    // falls back to its first Team when this one no longer belongs.
    const savedTeam = localStorage.getItem(AuthenticateParams.TEAM_ID)
    if (savedTeam === ALL_TEAMS) this.setActiveTeam(null)
    else if (savedTeam !== null && Number(savedTeam) > 0) this.setActiveTeam(Number(savedTeam))
  }

  /**
   * Process login response — store token + memberships.
   * Cross-org migration: always returns 'dashboard'; the multi-membership
   * org-picker fallback is retired because every list page now scopes via
   * `useTeamFilter` and every settings page via `<TeamPickerGate>`. Single-
   * membership auto-select still writes through `setActiveOrg` so legacy
   * callers that read `activeOrgId` keep functioning during the transition.
   */
  public processLoginResponse(loginResp: {
    token: string
    userInfo: AuthUserProfile
    memberships?: MembershipInfo[]
  }): 'dashboard' | 'org-picker' {
    this.accessToken = loginResp.token
    localStorage.setItem(AuthenticateParams.ACCESS_TOKEN, loginResp.token)
    this.memberships = loginResp.memberships ?? []
    this.userDetail = loginResp.userInfo
    this.isAuthBootstrapped = true

    this.hydrateOrgContext()
    return 'dashboard'
  }

  /**
   * Fetch org memberships when we don't have them yet in store (e.g. after full reload).
   * Cross-org migration: no longer auto-selects on multi-membership; the org
   * picker is dead in the post-login flow.
   */
  public async fetchMyMemberships(): Promise<void> {
    const memberships = await getMyMemberships()
    this.memberships = memberships ?? []
    this.hydrateOrgContext()
  }

  /**
   * Login — authenticate globally, receive memberships.
   * Returns 'dashboard' | 'org-picker' | false based on result.
   */
  public async login(usernameOrEmail: string, password: string): Promise<string | false> {
    const currentOrg = this.rootStore.organizationStore.organization

    try {
      const loginData: ILoginRequest = { password }
      const isEmail = isValidEmail(trim(usernameOrEmail))
      if (isEmail === true) {
        loginData.email = trim(usernameOrEmail)
      } else {
        loginData.username = trim(usernameOrEmail)
      }
      const loginResp = await login(loginData)

      this.isDisabled = loginResp?.userInfo?.disabled ?? false
      if (loginResp?.userInfo?.disabled) {
        return false
      }

      if (loginResp?.userInfo.isResetPassword) {
        this.resetPasswordToken = loginResp?.userInfo.resetPasswordToken
        return false
      }

      const result = this.processLoginResponse(loginResp)

      const groupMembers = await getLoggedInUserGroupMembers()
      this.rootStore.groupStore.setGroupMembers(groupMembers)
      this.rootStore.userStore.setCurrentUserGroupMembers(groupMembers)
      this.rootStore.userStore.setCurrentUser(loginResp?.userInfo)

      return result
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string; details?: unknown } } } }
      localStorage.removeItem(AuthenticateParams.ACCESS_TOKEN)
      const serverMessage = err?.response?.data?.error?.message
      const details = err?.response?.data?.error?.details
      const code = details && typeof details === 'object' ? (details as { code?: string }).code : undefined
      const loginErr = new Error(serverMessage || Errors.LOGIN_FAILED) as Error & { code?: string }
      if (typeof code === 'string') loginErr.code = code
      throw loginErr
    }
  }

  public logout(): void {
    fcmTokenManager.clearToken()
    // Targeted clear of all auth/session keys on this origin (tokens, SSO
    // mid-flow markers, profile selection). Keeps non-auth localStorage
    // (user preferences, draft data) intact.
    clearAuthClientPersistence()
    try {
      sessionStorage.clear()
    } catch {
      // ignore — sessionStorage is tab-scoped anyway
    }

    this.accessToken = ''
    this.fcmToken = ''
    this.userDetail = {} as AuthUserProfile
    this.memberships = []
    this.activeOrgId = null
    this.currentMembership = null
    this.currentMembershipId = null
    this.resetPasswordToken = undefined
    this.isDisabled = false
    this.isAuthBootstrapped = false

    this.rootStore.groupStore.setGroupMembers([])
    this.rootStore.userStore.setCurrentUserGroupMembers([])
    this.rootStore.userStore.setCurrentUser({} as AuthUserProfile)

    // Flat-host logout: same-origin redirect — no subdomain manipulation. The
    // index.tsx handler keys off ?logout=1 to clear localStorage on this origin.
    window.location.href = `${window.location.origin}${routes.login.value}?logout=1`
  }

  /**
   * Activate one of the user's athlete profiles for subsequent requests.
   * Keeps the same JWT — only sets the X-Active-Profile-Id header (via
   * localStorage) and reloads memberships so the active org list reflects
   * the chosen profile.
   */
  public async switchToAthleteProfile(athleteProfileId: number): Promise<void> {
    // Sync BOTH the in-memory selection AND localStorage. The header/switcher observe
    // profileStore.activeProfile; the API interceptor reads X-Active-Profile-* from
    // localStorage. Writing only localStorage left activeProfile stale, so direct callers
    // (e.g. the add-profile modal) fell back to profiles[0] — the guardian's player —
    // after creating a child. setActiveProfile writes the same localStorage keys.
    this.rootStore.profileStore.setActiveProfile({ id: athleteProfileId, type: ActiveProfileType.ATHLETE })
    this.activeOrgId = null
    this.currentMembership = null
    this.currentMembershipId = null
    this.memberships = []
    await this.fetchMyMemberships()
    this.hydrateOrgContext()
    // Drop downstream org-scoped caches so the new profile never observes the
    // prior profile's processes/collections/training plans.
    clearOrgScopedStores()
    // Trigger notification sync for the new profile (void — switch must not block on sync).
    void this.rootStore.notificationStore.syncAfterProfileSwitch(`athlete-${athleteProfileId}`)
  }

  /**
   * Coach-symmetric counterpart of switchToAthleteProfile. Same JWT, only the
   * X-Active-Profile-* headers change; memberships refetch so the org list
   * reflects whatever the coach profile has access to.
   */
  public async switchToCoachProfile(coachProfileId: number): Promise<void> {
    // Sync in-memory selection + localStorage together (see switchToAthleteProfile).
    this.rootStore.profileStore.setActiveProfile({ id: coachProfileId, type: ActiveProfileType.COACH })
    this.activeOrgId = null
    this.currentMembership = null
    this.currentMembershipId = null
    this.memberships = []
    await this.fetchMyMemberships()
    this.hydrateOrgContext()
    // Drop downstream org-scoped caches so the new profile never observes the
    // prior profile's processes/collections/training plans.
    clearOrgScopedStores()
    // Trigger notification sync for the new profile (void — switch must not block on sync).
    void this.rootStore.notificationStore.syncAfterProfileSwitch(`coach-${coachProfileId}`)
  }

  /** Clear the active profile selection — requests fall back to the user's primary identity. */
  public clearActiveProfile(): void {
    localStorage.removeItem(AuthenticateParams.ACTIVE_PROFILE_ID)
    localStorage.removeItem(AuthenticateParams.ACTIVE_PROFILE_TYPE)
  }

  public async fetchauthRoles(): Promise<void> {
    this.authRoles = await getAuthRoles()
  }

  public setShowSurveyPopup(value: boolean): void {
    this.isShowSurveyPopup = value
  }
}
